import { Box, Paragraph, Table, Text } from '@zillow/constellation-10';
import { chunk, groupBy, orderBy, uniqBy } from 'lodash-es';
import type { ReactNode } from 'react';

export const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export type PaymentGridCell = {
  year: number;
  month: number; // 1-12
  cell: ReactNode;
  code: string;
  legend: string;
};

export function PaymentNoDataCell({ title }: { title?: string }) {
  return (
    <Box
      title={title}
      css={{
        width: '16px',
        height: '16px',
        backgroundColor: 'white',
        backgroundImage: `repeating-linear-gradient(45deg,#D1D1D5 0, #D1D1D5 0.5px, transparent 1px, transparent 3px),
     repeating-linear-gradient(-45deg, #D1D1D5 0, #D1D1D5 0.5px, transparent 1px, transparent 3px)`,
      }}
    />
  );
}

function MonthLabel({ month }: { month: ReactNode }) {
  return (
    <Table.HeaderCell
      css={{
        color: 'text.subtle',
        textAlign: 'center',
        paddingLeft: 0,
        paddingRight: 0,
        fontWeight: 'bold',
      }}
    >
      {month}
    </Table.HeaderCell>
  );
}

function MonthCell({ yearCells, monthIndex }: { yearCells: Array<PaymentGridCell>; monthIndex: number }) {
  const monthNum = monthIndex + 1;
  const match = yearCells.find((c) => c.month === monthNum);
  return (
    <Table.Cell
      style={{
        paddingLeft: 0,
        paddingRight: 0,
        verticalAlign: 'center',
      }}
    >
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {match?.cell ?? <PaymentNoDataCell title="No history provided" />}
      </Box>
    </Table.Cell>
  );
}

interface PaymentHistoryGridYear {
  year: number;
  yearCells: Array<PaymentGridCell>;
}

function PaymentHistoryGridTableSmall({ years }: { years: Array<PaymentHistoryGridYear> }) {
  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'sm',
      }}
    >
      {years.map(({ year, yearCells }) => (
        <Table.Root
          key={year}
          aria-label="Payment history"
          appearance="horizontal"
          style={{
            tableLayout: 'fixed',
          }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                css={{
                  color: 'text.subtle',
                  backgroundColor: 'backgroundLight',
                  textAlign: 'center',
                  paddingTop: 0,
                  paddingBottom: 0,
                }}
                colSpan={6}
              >
                {year}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {chunk(
              MONTH_LABELS.map((monthLabel, monthIndex) => ({ monthLabel, monthIndex })),
              6,
            ).map((months) => (
              <>
                <Table.Row>
                  {months.map(({ monthLabel }) => (
                    <MonthLabel key={monthLabel} month={monthLabel} />
                  ))}
                </Table.Row>
                <Table.Row key={year}>
                  {months.map(({ monthIndex }) => (
                    <MonthCell key={monthIndex} monthIndex={monthIndex} yearCells={yearCells} />
                  ))}
                </Table.Row>
              </>
            ))}
          </Table.Body>
        </Table.Root>
      ))}
    </Box>
  );
}

function PaymentHistoryGridTableLarge({ years }: { years: Array<PaymentHistoryGridYear> }) {
  return (
    <Table.Root
      aria-label="Payment history"
      alignments={['start', ...MONTH_LABELS.map(() => 'center' as const)]}
      appearance="horizontal"
      style={{
        tableLayout: 'fixed',
      }}
    >
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell> </Table.HeaderCell>
          {MONTH_LABELS.map((month) => (
            <MonthLabel key={month} month={month} />
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {years.map(({ year, yearCells }) => (
          <Table.Row key={year}>
            <Table.HeaderCell
              css={{
                color: 'text.subtle',
                textStyle: 'body-sm-bold',
              }}
            >
              {year}
            </Table.HeaderCell>
            {MONTH_LABELS.map((_, i) => (
              <MonthCell key={i} monthIndex={i} yearCells={yearCells} />
            ))}
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}
function PaymentHistoryGridTable({ cells }: { cells: Array<PaymentGridCell> }) {
  const years = Object.entries(groupBy(cells, 'year'))
    .map(([year, yearCells]) => ({ year: Number(year), yearCells }))
    .sort((a, b) => a.year - b.year);

  return (
    <Box css={{ gridArea: 'table' }}>
      <Box
        css={{
          display: {
            sm: 'block',
            lg: 'none',
          },
        }}
      >
        <PaymentHistoryGridTableSmall years={years} />
      </Box>
      <Box
        css={{
          overflowX: 'auto',
          display: {
            sm: 'none',
            lg: 'block',
          },
        }}
      >
        <PaymentHistoryGridTableLarge years={years} />
      </Box>
    </Box>
  );
}

function PaymentHistoryGridLegend({ cells }: { cells: Array<PaymentGridCell> }) {
  const NO_DATA_LEGEND = 'No data for this period';
  const legendItems = uniqBy(
    [
      ...orderBy(cells, ['year', 'month']).filter((c) => c.legend),
      { legend: NO_DATA_LEGEND, cell: <PaymentNoDataCell /> },
    ],
    (c) => c.legend,
  );

  return (
    <Box css={{ gridArea: 'legend', display: 'flex', flexWrap: 'wrap', rowGap: 'xs' }}>
      {legendItems.map(({ legend, cell }) => (
        <Box
          key={legend}
          css={{
            display: 'flex',
            alignItems: 'center',
            paddingRight: 'md',
            gap: 'xs',
            flexBasis: {
              sm: '50%',
              lg: 'auto',
            },
          }}
        >
          {cell}
          <Text textStyle="body-xs">{legend}</Text>
        </Box>
      ))}
    </Box>
  );
}

export function PaymentHistoryGridCommon({ cells }: { cells: Array<PaymentGridCell> }) {
  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'sm', marginTop: 'sm', maxWidth: '800px' }}>
      <Box>
        <Paragraph textStyle="body-sm-bold">Payment history</Paragraph>
      </Box>
      <Box
        css={{
          display: 'grid',
          gap: 'sm',
          gridTemplateAreas: {
            sm: '"legend" "table"',
            lg: '"table" "legend"',
          },
        }}
      >
        <PaymentHistoryGridTable cells={cells} />
        <PaymentHistoryGridLegend cells={cells} />
      </Box>
    </Box>
  );
}
